import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Memory management utilities
 */
export const memoryUtils = {
  /**
   * Clear all caches to free up memory
   */
  clearAllCaches: () => {
    // Clear localStorage caches
    const keysToRemove = [
      'wouldYouRather_usedQuestions',
      'gameQuestionCache',
      'cdnCache'
    ];
    
    keysToRemove.forEach(key => {
      try {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      } catch (error) {
        console.warn('Failed to clear cache key:', key, error);
      }
    });

    // Clear service caches
    try {
      // CDN-based loading uses GameQuestionManager
      // Clear any remaining caches
      import('../services/GameQuestionManager').then(module => {
        if (module.GameQuestionManager && module.GameQuestionManager.clearCache) {
          module.GameQuestionManager.clearCache();
        }
      });
    } catch (error) {
      console.warn('Failed to clear service caches:', error);
    }
  },

  /**
   * Get memory usage info (if available)
   */
  getMemoryInfo: () => {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
        total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
        limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024)
      };
    }
    return null;
  },

  /**
   * Get comprehensive memory and performance info
   */
  getComprehensiveMemoryInfo: () => {
    const jsMemory = memoryUtils.getMemoryInfo();
    
    // Count DOM nodes
    const domNodeCount = document.querySelectorAll('*').length;
    
    // Count event listeners (approximate)
    const eventListenerCount = memoryUtils.getEventListenerCount();
    
    // Get image memory usage
    const imageMemory = memoryUtils.getImageMemoryUsage();
    
    // Get localStorage/sessionStorage usage
    const storageUsage = memoryUtils.getStorageUsage();
    
    // Get WebGL memory (if available)
    const webglMemory = memoryUtils.getWebGLMemory();
    
    // Get audio/video memory
    const mediaMemory = memoryUtils.getMediaMemory();
    
    return {
      jsMemory,
      domNodeCount,
      eventListenerCount,
      imageMemory,
      storageUsage,
      webglMemory,
      mediaMemory,
      timestamp: Date.now()
    };
  },

  /**
   * Estimate event listener count
   */
  getEventListenerCount: () => {
    // This is an approximation - we can't directly count event listeners
    // but we can check for common patterns that might indicate leaks
    const elementsWithListeners = document.querySelectorAll('[onclick], [onchange], [onsubmit], [onload], [onerror]');
    return elementsWithListeners.length;
  },

  /**
   * Get image memory usage
   */
  getImageMemoryUsage: () => {
    const images = document.querySelectorAll('img');
    let totalSize = 0;
    let loadedImages = 0;
    
    images.forEach(img => {
      if (img.complete && img.naturalWidth > 0) {
        // Estimate memory usage: width * height * 4 bytes (RGBA)
        totalSize += img.naturalWidth * img.naturalHeight * 4;
        loadedImages++;
      }
    });
    
    return {
      totalSize: Math.round(totalSize / 1024 / 1024), // MB
      imageCount: loadedImages,
      totalImages: images.length
    };
  },

  /**
   * Get storage usage
   */
  getStorageUsage: () => {
    let localStorageSize = 0;
    let sessionStorageSize = 0;
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          localStorageSize += key.length + (localStorage.getItem(key)?.length || 0);
        }
      }
      
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          sessionStorageSize += key.length + (sessionStorage.getItem(key)?.length || 0);
        }
      }
    } catch (error) {
      console.warn('Failed to calculate storage usage:', error);
    }
    
    return {
      localStorage: Math.round(localStorageSize / 1024), // KB
      sessionStorage: Math.round(sessionStorageSize / 1024), // KB
      localStorageKeys: localStorage.length,
      sessionStorageKeys: sessionStorage.length
    };
  },

  /**
   * Get WebGL memory usage (if available)
   */
  getWebGLMemory: () => {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (gl && 'getExtension' in gl) {
        const ext = gl.getExtension('WEBGL_debug_renderer_info');
        if (ext) {
          return {
            renderer: gl.getParameter(ext.UNMASKED_RENDERER_WEBGL),
            vendor: gl.getParameter(ext.UNMASKED_VENDOR_WEBGL)
          };
        }
      }
    } catch (error) {
      // WebGL not available
    }
    return null;
  },

  /**
   * Get audio/video memory usage
   */
  getMediaMemory: () => {
    const audioElements = document.querySelectorAll('audio');
    const videoElements = document.querySelectorAll('video');
    
    return {
      audioCount: audioElements.length,
      videoCount: videoElements.length,
      playingAudio: Array.from(audioElements).filter(a => !a.paused).length,
      playingVideo: Array.from(videoElements).filter(v => !v.paused).length
    };
  },

  /**
   * Check for potential memory leaks
   */
  checkForMemoryLeaks: () => {
    const info = memoryUtils.getComprehensiveMemoryInfo();
    const warnings = [];
    
    // Check for excessive DOM nodes
    if (info.domNodeCount > 1000) {
      warnings.push(`High DOM node count: ${info.domNodeCount} (consider cleanup)`);
    }
    
    // Check for excessive event listeners
    if (info.eventListenerCount > 100) {
      warnings.push(`Many inline event listeners: ${info.eventListenerCount} (consider delegation)`);
    }
    
    // Check for large image memory usage
    if (info.imageMemory.totalSize > 50) {
      warnings.push(`Large image memory: ${info.imageMemory.totalSize}MB (consider lazy loading)`);
    }
    
    // Check for excessive storage usage
    if (info.storageUsage.localStorage > 1000 || info.storageUsage.sessionStorage > 1000) {
      warnings.push(`Large storage usage: ${info.storageUsage.localStorage + info.storageUsage.sessionStorage}KB`);
    }
    
    // Check for playing media
    if (info.mediaMemory.playingAudio > 0 || info.mediaMemory.playingVideo > 0) {
      warnings.push(`Active media: ${info.mediaMemory.playingAudio} audio, ${info.mediaMemory.playingVideo} video`);
    }
    
    return warnings;
  },

  /**
   * Detect and log memory leaks in real-time
   */
  detectMemoryLeaks: () => {
    const leaks = [];
    
    // Check for uncleaned event listeners
    const elementsWithListeners = document.querySelectorAll('[onclick], [onchange], [onsubmit], [onload], [onerror]');
    if (elementsWithListeners.length > 50) {
      leaks.push(`⚠️ Many inline event listeners: ${elementsWithListeners.length}`);
    }
    
    // Check for audio elements that might be leaking
    const audioElements = document.querySelectorAll('audio');
    const playingAudio = Array.from(audioElements).filter(a => !a.paused);
    if (playingAudio.length > 0) {
      leaks.push(`⚠️ ${playingAudio.length} audio elements still playing`);
    }
    
    // Check for video elements
    const videoElements = document.querySelectorAll('video');
    const playingVideo = Array.from(videoElements).filter(v => !v.paused);
    if (playingVideo.length > 0) {
      leaks.push(`⚠️ ${playingVideo.length} video elements still playing`);
    }
    
    // Check for large objects in memory
    const memoryInfo = memoryUtils.getMemoryInfo();
    if (memoryInfo && memoryInfo.used > 200) {
      leaks.push(`⚠️ High memory usage: ${memoryInfo.used}MB`);
    }
    
    // Check for excessive DOM nodes
    const domNodeCount = document.querySelectorAll('*').length;
    if (domNodeCount > 1000) {
      leaks.push(`⚠️ High DOM node count: ${domNodeCount}`);
    }
    
    // Check for large storage usage
    let localStorageSize = 0;
    let sessionStorageSize = 0;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          localStorageSize += key.length + (localStorage.getItem(key)?.length || 0);
        }
      }
      
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          sessionStorageSize += key.length + (sessionStorage.getItem(key)?.length || 0);
        }
      }
    } catch (error) {
      // Ignore storage errors
    }
    
    if (localStorageSize > 500000 || sessionStorageSize > 500000) { // 500KB
      leaks.push(`⚠️ Large storage usage: ${Math.round((localStorageSize + sessionStorageSize) / 1024)}KB`);
    }
    
    // Log leaks to console
    if (leaks.length > 0) {
      console.group('🔍 Memory Leak Detection');
      leaks.forEach(leak => console.warn(leak));
      console.groupEnd();
    }
    
    return leaks;
  },

  /**
   * Start continuous memory leak monitoring
   */
  startMemoryLeakMonitoring: () => {
    const interval = setInterval(() => {
      memoryUtils.detectMemoryLeaks();
    }, 10000); // Check every 10 seconds
    
    // Store interval ID for cleanup
    (window as any).__memoryLeakMonitorInterval = interval;
    
    console.log('🔍 Memory leak monitoring started');
  },

  /**
   * Stop memory leak monitoring
   */
  stopMemoryLeakMonitoring: () => {
    if ((window as any).__memoryLeakMonitorInterval) {
      clearInterval((window as any).__memoryLeakMonitorInterval);
      (window as any).__memoryLeakMonitorInterval = null;
      console.log('🔍 Memory leak monitoring stopped');
    }
  },

  /**
   * Force garbage collection (if available)
   */
  forceGC: () => {
    if ('gc' in window) {
      (window as any).gc();
    }
  }
};
